import classNames from "classnames";
import React, { useContext, useEffect, useState } from "react";
import { COMMENT_SIDEBAR_MIN_WIDTH } from "src/components/TiptapDocumentSB/commentSidebarWidth";
import { AutoExpandTextarea } from "src/components/TiptapDocumentSB/AutoExpandTextarea";
import { MAX_DOCUMENT_TITLE_LENGTH } from "../../../../../../src/shared/constants";
import { useProjectStore } from "../../../../stores/projectStore";
import { useSudowriteStore } from "../../../../stores/sudowriteStore";
import { sendAppEvent } from "../../../../Utils";
import { EditorContainerContext } from "../EditorContainerContext";
import { StyledContainer } from "./styles";

export const EditableDocumentTitle = React.forwardRef(({ project, documentId, contents, editorComputedStyles, editor, insertEditor, hasComments = false, commentSidebarWidth = COMMENT_SIDEBAR_MIN_WIDTH }, ref) => {
  const { titleEditorRef, height } = useContext(EditorContainerContext);
  const [editableTitle, setEditableTitle] = useState("");
  const setIsEditingDocumentTitle = useProjectStore((state) => state.setIsEditingDocumentTitle);
  const subscription = useSudowriteStore((state) => state.subscription);

  useEffect(() => {
    const oldTitle = document.title;

    if (!editableTitle) {
      document.title = "Sudowrite";
    } else {
      document.title = `Sudowrite - ${editableTitle}`;
    }

    return () => {
      document.title = oldTitle;
    };
  }, [editableTitle]);

  const getTitle = (contents) => {
    return contents?.find((item) => item.id === documentId)?.title;
  };

  const isUntitled = (str) => {
    return /^Untitled( \d+)?$/.test(str);
  };

  useEffect(() => {
    const title = getTitle(contents);
    setEditableTitle(title || "Untitled");
  }, [contents, documentId]);

  const handleTitleChange = (e) => {
    // Remove any leading newlines
    const newTitle = e.target.value.replace(/^\n+/, "");

    const newlineIndex = newTitle.indexOf("\n");
    let remainingString = "";

    if (newlineIndex !== -1) {
      const end = newlineIndex < MAX_DOCUMENT_TITLE_LENGTH ? newlineIndex : MAX_DOCUMENT_TITLE_LENGTH;
      // set the title only to the first line of the value less than the max length
      setEditableTitle(newTitle.substring(0, end));
      // save the remaining string to insert into the main document editor
      remainingString = newTitle.substring(end);
    } else {
      if (newTitle.length > MAX_DOCUMENT_TITLE_LENGTH) {
        setEditableTitle(newTitle.substring(0, MAX_DOCUMENT_TITLE_LENGTH));
        remainingString = newTitle.substring(MAX_DOCUMENT_TITLE_LENGTH);
      } else {
        setEditableTitle(newTitle);
      }
    }

    if (remainingString) {
      // insert any remaining text into the main document editor
      insertEditor({ text: remainingString, colored: false });
    }
  };

  const handleTitleFocus = () => {
    setIsEditingDocumentTitle(true);
  };

  const handleTitleBlur = () => {
    setIsEditingDocumentTitle(false);

    sendAppEvent("Document Title Changed", { position: "Editable Document Title" });

    // Trigger the update callback when the title loses focus
    if (documentId) {
      project.updateDocument({
        id: documentId,
        title: editableTitle,
      });
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();

      // Focus the editor
      editor.view.focus();
    }
    if (e.key === "ArrowDown") {
      const field = titleEditorRef.current;
      // only move cursor down if the cursor is at the end of the title
      if (field.selectionStart === field.value.length) {
        e.preventDefault();
        editor.view.focus();
      }
    }
  };

  const handleClick = (e) => {
    if (isUntitled(e.target.value)) {
      e.target.select();
    }
  };

  return (
    <StyledContainer
      className={classNames("container", { "chapter-glue-beta": subscription?.hasAccessToChapterGlueBeta })}
      ref={ref}
      id="title-text-wrapper"
      offset={height?.offset || 0}
      isSingleLine={height?.isSingleLine}
      $hasComments={hasComments}
      $commentSidebarWidth={commentSidebarWidth}
    >
      <AutoExpandTextarea
        type="text"
        className={classNames({ untitled: isUntitled(editableTitle) })}
        style={{ fontFamily: editorComputedStyles.fontFamily }}
        value={editableTitle}
        onChange={handleTitleChange}
        onBlur={handleTitleBlur}
        onFocus={handleTitleFocus}
        onKeyDown={handleKeyDown}
        onClick={handleClick}
        ariaLabel="Edit Title"
      />
    </StyledContainer>
  );
});

export default EditableDocumentTitle;
